import { __decorate } from "tslib";
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { ElementDiscoveryController } from '../../base/elements';
import { getSlottedChildren } from '../../utils/dom';
import { isNil, isUndefined } from '../../utils/unit';
import { MediaController } from '../controller';
import { ControlsManager } from './controls';
import { IdleManager } from './idle';
import { basePlayerStyles } from './styles';
export function WithMediaPlayer(Provider) {
    class MediaPlayer extends Provider {
        constructor() {
            super(...arguments);
            this.controller = new MediaController(this);
            // -------------------------------------------------------------------------------------------
            // Controls
            // -------------------------------------------------------------------------------------------
            this.controlsManager = new ControlsManager(this);
            this.idleManager = new IdleManager(this);
        }
        static get styles() {
            // Provider styles should override base media styles.
            // @ts-expect-error
            if (super.styles)
                return [basePlayerStyles, super.styles];
            return [basePlayerStyles];
        }
        connectedCallback() {
            super.connectedCallback();
            new ElementDiscoveryController(this, 'vds-media-player-connect');
        }
        // -------------------------------------------------------------------------------------------
        // Properties
        // -------------------------------------------------------------------------------------------
        get logLevel() {
            return this.controller.logLevel;
        }
        set logLevel(newLevel) {
            this.controller.logLevel = newLevel;
        }
        get customControls() {
            return !this.controlsManager.isHidden;
        }
        set customControls(isShowing) {
            if (isShowing) {
                this.controlsManager.show();
            }
            else {
                this.controlsManager.hide();
            }
        }
        get idleTimeout() {
            return this.idleManager.timeout;
        }
        set idleTimeout(timeout) {
            this.idleManager.timeout = timeout;
        }
        // -------------------------------------------------------------------------------------------
        // Render
        // -------------------------------------------------------------------------------------------
        render() {
            return this.renderPlayer();
        }
        renderPlayer() {
            return html `${this.renderProvider()}${this.renderUi()}`;
        }
        renderProvider() {
            return this._renderMediaSlot();
        }
        renderUi() {
            return html `<slot name="ui"></slot>`;
        }
        // -------------------------------------------------------------------------------------------
        // Media Slot
        // -------------------------------------------------------------------------------------------
        /**
         * This slot is not required for the player to work. It only introduces support for
         * additional players in the ecosystem to plug in and work with this player.
         */
        _renderMediaSlot() {
            return html `
        <slot name="media" @slotchange="${this._handleMediaSlotChange}"></slot>
      `;
        }
        _handleMediaSlotChange() {
            // This is a media provider.
            if (!isUndefined(this.play))
                return;
            const mediaProvider = getSlottedChildren(this, 'media')[0];
            // Not a bulletproof check, but it's a good enough safety-check to warn devs if they pass the
            // wrong element.
            if (!isNil(mediaProvider) && isUndefined(mediaProvider.play)) {
                throw Error('Invalid media element given to `media` slot.');
            }
            this.controller.setMediaProvider(mediaProvider);
        }
    }
    __decorate([
        property({ attribute: 'log-level' })
    ], MediaPlayer.prototype, "logLevel", null);
    __decorate([
        property({ attribute: 'custom-controls', type: Boolean, reflect: true })
    ], MediaPlayer.prototype, "customControls", null);
    __decorate([
        property({ attribute: 'idle-timeout', type: Number })
    ], MediaPlayer.prototype, "idleTimeout", null);
    return MediaPlayer;
}
